import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';

// Split/bonus adjustment for the charted stock. Upstox's historical candles
// are not adjusted, so a 1:5 split shows as an 80% cliff on the ex-date until
// the older candles are divided by 5 here (volume is multiplied by 5). The
// backend keeps a copy of the unadjusted prices and the nightly Upstox refresh
// re-applies the adjustment, so it survives being overwritten.
const SplitAdjustBox = ({ link, onChanged, onClose }) => {
  const [adjustments, setAdjustments] = useState([]);
  const [exDate, setExDate] = useState('');
  const [ratio, setRatio] = useState('');
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!link) return undefined;
    let cancelled = false;
    setMessage('');
    setHint('');
    apiFetch(`/api/split-adjust/${encodeURIComponent(link)}`)
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (cancelled) return;
        if (!ok) throw new Error(body.error || 'Could not load adjustments');
        setAdjustments(body.adjustments || []);
        // Prefill with the biggest overnight gap - usually the ex-date.
        const s = body.suggestion;
        if (s && s.gap) {
          const known = (body.adjustments || []).some((a) => a.ex_date === s.ex_date);
          setExDate(s.ex_date);
          setRatio(known ? '' : String(Math.round(s.gap * 100) / 100));
          setHint(`Biggest gap: ${s.ex_date}, ${s.prev_close.toFixed(2)} → ${s.open.toFixed(2)} (×${s.gap.toFixed(2)})${known ? ', already adjusted' : ''}`);
        } else {
          setExDate('');
          setRatio('');
        }
      })
      .catch((err) => { if (!cancelled) setMessage(err.message); });
    return () => { cancelled = true; };
  }, [link]);

  const send = async (method, path, body) => {
    setBusy(true);
    setMessage('');
    try {
      const response = await apiFetch(path, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Adjustment failed');
      setAdjustments(result.adjustments || []);
      setMessage(`Updated ${result.daily} daily and ${result.monthly} monthly candles`);
      onChanged();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  const apply = (event) => {
    event.preventDefault();
    send('POST', `/api/split-adjust/${encodeURIComponent(link)}`, { exDate, ratio: Number(ratio) });
  };

  return (
    <div className="split-adjust-box" onClick={(event) => event.stopPropagation()}>
      <div className="split-adjust-head">
        <strong>Split / bonus · {link}</strong>
        <button type="button" onClick={onClose} title="Close">×</button>
      </div>
      <form onSubmit={apply}>
        <label>
          Ex-date
          <input type="date" value={exDate} onChange={(e) => setExDate(e.target.value)} required />
        </label>
        <label title="New shares per old share: 5 for a 1:5 split (₹10 → ₹2), 2 for a 1:1 bonus, 1.5 for a 1:2 bonus">
          Ratio
          <input type="number" step="any" min="0" value={ratio} onChange={(e) => setRatio(e.target.value)} placeholder="5" required />
        </label>
        <button type="submit" disabled={busy || !exDate || !(Number(ratio) > 0)}>{busy ? '…' : 'Adjust'}</button>
      </form>
      {hint && <div className="split-adjust-hint">{hint}</div>}
      <div className="split-adjust-hint">Prices before the ex-date ÷ ratio, volume × ratio.</div>
      {adjustments.length > 0 && (
        <ul className="split-adjust-list">
          {adjustments.map((a) => (
            <li key={a.ex_date}>
              {a.ex_date} · ×{a.ratio}
              <button
                type="button"
                disabled={busy}
                onClick={() => send('DELETE', `/api/split-adjust/${encodeURIComponent(link)}/${a.ex_date}`)}
                title="Undo: put the original Upstox prices back"
              >
                Undo
              </button>
            </li>
          ))}
        </ul>
      )}
      {message && <div className="split-adjust-msg">{message}</div>}
    </div>
  );
};

export default SplitAdjustBox;
